// Phase I4.3 — Daily Acca · Direction B (Split Intelligence).
// Faithful port of prototype/daily-acca.jsx::DailyAccaB. Free Acca on
// the left, locked Pro pane on the right (collapsed when isPro=true),
// Founder's Pick as a bottom band.

import { ConfMeter, Eyebrow, Badge } from "../../../components/super-acca";
import { EmptyAccaCard, EmptyFounderCard, LoadingCard } from "./EmptyStates.tsx";
import { legToVM } from "./legViewModel.ts";
import { marketLabel, selectionLabel } from "../../../exchange/market-format.ts";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import type { DailyAccaDirectionProps } from "./DailyAccaA.tsx";

const fontDisplay = "var(--sa-font-display)";
const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";
const fontStat = "var(--sa-font-stat)";

export function DailyAccaB({ acca, founder, isPro, loading }: DailyAccaDirectionProps): JSX.Element {
  return (
    <div style={{ padding: "32px 40px 72px", maxWidth: 1180, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          paddingBottom: 22,
          borderBottom: "1px solid var(--sa-hairline)",
        }}
      >
        <div style={{ flex: 1 }}>
          <Eyebrow>Daily Window · {acca?.acca_date ?? "Today"}</Eyebrow>
          <div style={{ fontFamily: fontDisplay, fontSize: 38, fontWeight: 700, marginTop: 10, lineHeight: 1.05 }}>
            One free {isPro ? "5-leg" : "3-leg"}. One Founder's Pick.{" "}
            <span style={{ color: "var(--sa-accent-saffron)" }}>
              {isPro ? "Pro slate, deeper edge." : "One Pro slip locked behind today's edge."}
            </span>
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: fontMono, fontSize: 10, color: "var(--sa-fg-muted)", letterSpacing: "0.18em" }}>
            NEXT REFRESH
          </div>
          <div style={{ fontFamily: fontStat, fontSize: 30, color: "var(--sa-fg-primary)", marginTop: 4, lineHeight: 1 }}>
            11:30 GMT
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ marginTop: 28 }}><LoadingCard /></div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isPro ? "1fr" : "1.5fr 1fr",
            gap: 24,
            marginTop: 28,
          }}
        >
          {/* LEFT — Free Acca */}
          {acca ? <FreeAccaPane acca={acca} isPro={isPro} /> : <EmptyAccaCard />}

          {/* RIGHT — Locked Pro pane (only for free users) */}
          {!isPro ? <ProUpsellPane combinedOdds={acca?.combined_odds ?? 0} /> : null}
        </div>
      )}

      {/* Founder's Pick band */}
      {!loading && (founder ? <FounderBand founder={founder} /> : <EmptyFounderCard />)}
    </div>
  );
}

function FreeAccaPane({ acca, isPro }: { acca: NonNullable<DailyAccaDirectionProps["acca"]>; isPro: boolean }): JSX.Element {
  return (
    <div style={{ border: "1px solid var(--sa-hairline)", background: "var(--sa-bg-panel)", borderRadius: 8, padding: 26 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 18 }}>
        <Eyebrow>Free Daily Acca · {acca.leg_count} Legs</Eyebrow>
        <div style={{ fontFamily: fontStat, fontSize: 40, color: "var(--sa-accent-saffron)", lineHeight: 1 }}>
          {acca.combined_odds.toFixed(2)}×
        </div>
      </div>
      <ConfMeter
        value={acca.confidence ?? 0}
        locked={!isPro}
        label={isPro ? "Composite Confidence" : "Composite Confidence — exact value Pro only"}
      />
      <div style={{ marginTop: 18, display: "flex", flexDirection: "column" }}>
        {acca.legs.map((rawLeg, i) => {
          const leg = legToVM(rawLeg, i, acca.confidence);
          const last = i === acca.legs.length - 1;
          return (
            <div
              key={`${rawLeg.fixture_id}|${i}`}
              style={{
                padding: "16px 0",
                display: "grid",
                gridTemplateColumns: "32px 1fr 90px 60px",
                gap: 14,
                alignItems: "center",
                borderBottom: last ? "none" : "1px solid var(--sa-hairline)",
              }}
            >
              <div style={{ fontFamily: fontMono, fontSize: 10, color: "var(--sa-accent-saffron)", letterSpacing: "0.16em", textAlign: "center" }}>
                L0{i + 1}
              </div>
              <div>
                <div style={{ fontFamily: fontBody, fontSize: 14, color: "var(--sa-fg-primary)", fontWeight: 500 }}>
                  {leg.homeShort} <span style={{ color: "var(--sa-fg-muted)" }}>×</span> {leg.awayShort}
                  <span style={{ color: "var(--sa-fg-muted)", fontSize: 11.5, marginLeft: 10, fontFamily: fontMono, letterSpacing: "0.1em" }}>
                    {leg.kickoff}
                  </span>
                </div>
                <div style={{ fontFamily: fontBody, fontSize: 12.5, color: "var(--sa-fg-secondary)", marginTop: 5 }}>
                  <span style={{ color: "var(--sa-accent-saffron)", fontFamily: fontMono, fontSize: 11, letterSpacing: 0.5 }}>
                    {leg.market} → {leg.selection}
                  </span>
                  <span style={{ color: "var(--sa-fg-muted)", marginLeft: 10 }}>{leg.reasoning}</span>
                </div>
              </div>
              <div style={{ fontFamily: fontMono, fontSize: 13, color: "var(--sa-fg-primary)", textAlign: "right" }}>
                {leg.odds.toFixed(2)}×
              </div>
              <div
                style={{
                  fontFamily: fontMono,
                  fontSize: 11,
                  textAlign: "right",
                  color: leg.legConf >= 75 ? "var(--sa-signal-green)" : "var(--sa-accent-saffron)",
                }}
              >
                {isPro ? leg.legConf : "‧‧"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProUpsellPane({ combinedOdds }: { combinedOdds: number }): JSX.Element {
  return (
    <div
      style={{
        border: "1px solid var(--sa-amber-dim)",
        borderTop: "2px solid var(--sa-accent-saffron)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
        padding: 26,
        position: "relative",
      }}
    >
      <Eyebrow>Pro · Super Acca</Eyebrow>
      <div style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-fg-muted)", marginTop: 14, letterSpacing: "0.14em" }}>
        SINGLE-MATCH BET BUILDER · TODAY
      </div>
      <div style={{ fontFamily: fontDisplay, fontSize: 26, fontWeight: 600, lineHeight: 1.15, marginTop: 8 }}>
        4× to 15× combined odds
      </div>
      <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Stat label="FREE TODAY" value={`${combinedOdds.toFixed(2)}×`} />
        <Stat label="PRO TODAY" value="UNLOCK →" accent />
      </div>
      <p style={{ marginTop: 18, fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-secondary)", lineHeight: 1.6 }}>
        Pro members see today's Super Acca — model probability vs market probability, per-leg
        reasoning, and the agent debate behind every leg.
      </p>
      <a
        href="/pricing"
        style={{
          display: "block",
          textAlign: "center",
          marginTop: 22,
          padding: "13px 22px",
          background: "var(--sa-accent-saffron)",
          color: "#0A0A0A",
          fontFamily: fontMono,
          fontSize: 11.5,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontWeight: 500,
          borderRadius: 4,
          textDecoration: "none",
        }}
      >
        UNLOCK FOR GH₵19/MO
      </a>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div style={{ padding: 14, background: "var(--sa-bg-raised)", border: "1px solid var(--sa-hairline)", borderRadius: 4 }}>
      <div style={{ fontFamily: fontMono, fontSize: 9.5, color: "var(--sa-fg-muted)", letterSpacing: "0.18em" }}>{label}</div>
      <div
        style={{
          fontFamily: fontStat,
          fontSize: 32,
          color: accent ? "var(--sa-accent-saffron)" : "var(--sa-fg-primary)",
          lineHeight: 1,
          marginTop: 6,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function FounderBand({ founder }: { founder: NonNullable<DailyAccaDirectionProps["founder"]> }): JSX.Element {
  const reasoning = summarizeReasoning(founder.reasoning) ?? "";
  return (
    <div
      style={{
        marginTop: 28,
        background: "#0E0B05",
        border: "1px solid var(--sa-amber-dim)",
        borderRadius: 8,
        padding: "28px 32px",
        display: "grid",
        gridTemplateColumns: "180px 1fr 220px",
        gap: 28,
        alignItems: "center",
      }}
    >
      <div>
        <Eyebrow>Founder's Pick</Eyebrow>
        <div style={{ fontFamily: fontMono, fontSize: 10.5, color: "var(--sa-fg-muted)", marginTop: 10, letterSpacing: "0.16em" }}>
          {founder.pick_date}
        </div>
      </div>
      <div>
        <div style={{ fontFamily: fontDisplay, fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>
          {founder.fixture_id} · <span style={{ color: "var(--sa-accent-saffron)" }}>{marketLabel(founder.market)}: {selectionLabel(founder.market, founder.selection)}</span>
        </div>
        {reasoning ? (
          <div style={{ fontFamily: fontBody, fontSize: 12.5, color: "var(--sa-fg-muted)", marginTop: 8, fontStyle: "italic", maxWidth: 620 }}>
            {reasoning}
          </div>
        ) : null}
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontFamily: fontStat, fontSize: 44, color: "var(--sa-accent-saffron)", lineHeight: 1 }}>
          {founder.odds.toFixed(2)}×
        </div>
        <div style={{ marginTop: 8 }}>
          <Badge variant="amber">FOUNDER'S PICK</Badge>
        </div>
      </div>
    </div>
  );
}
